/**
 * F5 (paridad Ledgeria) — Cuentas anuales: ECPN y EFE.
 *
 * Los dos estados enseñan su comprobación ARRIBA del todo, antes que las
 * cifras: un estado financiero que no cuadra con el libro no se lee, se
 * arregla. El ECPN se contrasta con el saldo de las cuentas de patrimonio y el
 * EFE con el movimiento real de la tesorería.
 */
import { useCallback, useEffect, useState } from 'react'
import { AlertCircle, Check, Download, Loader2, Scale } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { api, ApiError } from '../lib/api'
import { downloadFile } from '../lib/download'
import { formatCents } from '../lib/money'

const EQUITY_COLUMNS = ['capital', 'reserves', 'priorResults', 'yearResult'] as const
type EquityColumn = (typeof EQUITY_COLUMNS)[number]

interface EcpnResult {
  year: number
  rows: Array<{ key: string; amounts: Record<EquityColumn, number>; totalCents: number }>
  recognisedIncome: { yearResultCents: number; directToEquityCents: number; totalCents: number }
  check: { computedCents: number; ledgerCents: number; diffCents: number; matches: boolean }
}

interface CashFlowResult {
  year: number
  operatingDetail: {
    resultBeforeTaxCents: number
    depreciationCents: number
    disposalResultCents: number
    workingCapitalCents: number
  }
  sections: Array<{
    key: 'operating' | 'investing' | 'financing' | 'unclassified'
    totalCents: number
    lines: Array<{ code: string; name: string; amountCents: number }>
  }>
  netChangeCents: number
  openingCashCents: number
  closingCashCents: number
  check: {
    computedClosingCents: number
    ledgerClosingCents: number
    diffCents: number
    matches: boolean
  }
}

function errorText(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    return (err.body as { error?: string } | undefined)?.error ?? err.message
  }
  return err instanceof Error ? err.message : fallback
}

export default function AccountingAnnual() {
  const { t } = useTranslation(['annual', 'common'])
  const currentYear = new Date().getFullYear()
  const [year, setYear] = useState(currentYear - 1)
  const [ecpn, setEcpn] = useState<EcpnResult | null>(null)
  const [efe, setEfe] = useState<CashFlowResult | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [a, b] = await Promise.all([
        api.get<EcpnResult>(`/accounting/reports/ecpn?year=${year}`),
        api.get<CashFlowResult>(`/accounting/reports/cash-flow?year=${year}`),
      ])
      setEcpn(a)
      setEfe(b)
    } catch (err) {
      setError(errorText(err, t('errors.load')))
      setEcpn(null)
      setEfe(null)
    } finally {
      setLoading(false)
    }
  }, [year, t])

  useEffect(() => {
    void load()
  }, [load])

  const download = async (which: 'ecpn' | 'cash-flow') => {
    try {
      await downloadFile(
        `/accounting/reports/${which}/csv?year=${year}`,
        `${which === 'ecpn' ? 'ecpn' : 'flujos-efectivo'}-${year}.csv`
      )
    } catch (err) {
      setError(errorText(err, t('errors.download')))
    }
  }

  return (
    <div className="space-y-6 animate-slideUp">
      <header className="flex flex-wrap items-end gap-3 justify-between">
        <div>
          <h1 className="text-xl font-serif font-semibold t-primary flex items-center gap-2">
            <Scale size={20} className="text-indigo-600 dark:text-indigo-300" />
            {t('title')}
          </h1>
          <p className="text-xs t-muted">{t('subtitle')}</p>
        </div>
        <label className="text-xs t-muted flex flex-col gap-1">
          {t('year')}
          <select
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            className="bg-transparent border border-base rounded px-2 py-1 text-sm t-secondary focus:outline-none focus:border-toga-500/50"
          >
            {Array.from({ length: 8 }, (_, i) => currentYear - i).map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </label>
      </header>

      {error && (
        <div className="surface p-3 flex items-start gap-2 border-rose-500/40">
          <AlertCircle size={16} className="text-rose-400 mt-0.5 shrink-0" />
          <p className="text-sm text-rose-700 dark:text-rose-300">{error}</p>
        </div>
      )}

      {loading ? (
        <div className="t-muted flex items-center gap-2 text-sm">
          <Loader2 size={16} className="animate-spin" /> {t('calculating')}
        </div>
      ) : (
        <>
          {ecpn && (
            <section className="surface p-4 space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h2 className="text-base font-semibold t-primary">{t('ecpn.title', { year })}</h2>
                  <p className="text-xs t-muted">{t('ecpn.subtitle')}</p>
                </div>
                <button
                  type="button"
                  onClick={() => void download('ecpn')}
                  className="px-3 py-1.5 rounded-lg text-sm font-semibold text-white bg-toga-600 hover:bg-toga-700 flex items-center gap-1.5"
                >
                  <Download size={14} /> CSV
                </button>
              </div>

              <CheckBanner
                ok={ecpn.check.matches}
                okText={t('ecpn.checkOk')}
                koText={t('ecpn.checkKo', {
                  computed: formatCents(ecpn.check.computedCents),
                  ledger: formatCents(ecpn.check.ledgerCents),
                  diff: formatCents(ecpn.check.diffCents),
                })}
              />

              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-[11px] uppercase tracking-wider t-muted border-b border-base">
                      <th className="py-2 pr-3 font-medium">{t('ecpn.concept')}</th>
                      {EQUITY_COLUMNS.map((c) => (
                        <th key={c} className="py-2 px-2 text-right font-medium">
                          {t(`ecpn.columns.${c}`)}
                        </th>
                      ))}
                      <th className="py-2 pl-2 text-right font-medium">{t('ecpn.columns.total')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ecpn.rows.map((r) => {
                      const strong = r.key === 'opening' || r.key === 'closing'
                      return (
                        <tr
                          key={r.key}
                          className={`border-b border-base/50 last:border-0 ${strong ? 'font-semibold t-primary' : 't-secondary'}`}
                        >
                          <td className="py-2 pr-3">{t(`ecpn.rows.${r.key}`)}</td>
                          {EQUITY_COLUMNS.map((c) => (
                            <td key={c} className="py-2 px-2 text-right tabular-nums">
                              {r.amounts[c] ? formatCents(r.amounts[c]) : '—'}
                            </td>
                          ))}
                          <td className="py-2 pl-2 text-right tabular-nums t-primary">
                            {formatCents(r.totalCents)}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>

              <p className="text-[11px] t-faint">
                {t('ecpn.recognisedNote', {
                  result: formatCents(ecpn.recognisedIncome.yearResultCents),
                })}
              </p>
            </section>
          )}

          {efe && (
            <section className="surface p-4 space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h2 className="text-base font-semibold t-primary">{t('efe.title', { year })}</h2>
                  <p className="text-xs t-muted">{t('efe.subtitle')}</p>
                </div>
                <button
                  type="button"
                  onClick={() => void download('cash-flow')}
                  className="px-3 py-1.5 rounded-lg text-sm font-semibold text-white bg-toga-600 hover:bg-toga-700 flex items-center gap-1.5"
                >
                  <Download size={14} /> CSV
                </button>
              </div>

              <CheckBanner
                ok={efe.check.matches}
                okText={t('efe.checkOk')}
                koText={t('efe.checkKo', {
                  computed: formatCents(efe.check.computedClosingCents),
                  ledger: formatCents(efe.check.ledgerClosingCents),
                  diff: formatCents(efe.check.diffCents),
                })}
              />

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Metric label={t('efe.openingCash')} value={formatCents(efe.openingCashCents)} />
                <Metric label={t('efe.netChange')} value={formatCents(efe.netChangeCents)} />
                <Metric label={t('efe.closingCash')} value={formatCents(efe.closingCashCents)} accent />
                <Metric
                  label={t('efe.resultBeforeTax')}
                  value={formatCents(efe.operatingDetail.resultBeforeTaxCents)}
                />
              </div>

              {/* El desglose del método indirecto: del resultado al flujo. */}
              <div className="rounded-lg border border-base p-3 text-sm space-y-1">
                <h3 className="text-[11px] uppercase tracking-wider t-muted mb-1">
                  {t('efe.operatingDetail')}
                </h3>
                <DetailRow label={t('efe.detail.result')} value={efe.operatingDetail.resultBeforeTaxCents} />
                <DetailRow label={t('efe.detail.depreciation')} value={efe.operatingDetail.depreciationCents} />
                <DetailRow label={t('efe.detail.disposals')} value={efe.operatingDetail.disposalResultCents} />
                <DetailRow label={t('efe.detail.workingCapital')} value={efe.operatingDetail.workingCapitalCents} />
                <div className="flex items-center justify-between font-semibold t-primary pt-1 border-t border-base">
                  <span>{t('efe.sections.operating')}</span>
                  <span className="tabular-nums">
                    {formatCents(efe.sections.find((s) => s.key === 'operating')?.totalCents ?? 0)}
                  </span>
                </div>
              </div>

              {efe.sections
                .filter((s) => s.lines.length > 0)
                .map((s) => (
                  <div key={s.key}>
                    <h3 className="text-sm font-semibold t-secondary mb-1 flex items-center justify-between">
                      <span>{t(`efe.sections.${s.key}`)}</span>
                      <span className="tabular-nums font-mono text-xs t-primary">
                        {formatCents(s.totalCents)}
                      </span>
                    </h3>
                    <table className="w-full text-sm">
                      <tbody>
                        {s.lines.map((l) => (
                          <tr key={l.code} className="border-b border-base/40 last:border-0">
                            <td className="py-1 pr-3 font-mono text-xs t-muted w-20">{l.code}</td>
                            <td className="py-1 pr-3 text-xs t-secondary">{l.name}</td>
                            <td
                              className={`py-1 text-right tabular-nums text-xs ${
                                l.amountCents < 0
                                  ? 'text-rose-700 dark:text-rose-300'
                                  : 'text-emerald-700 dark:text-emerald-300'
                              }`}
                            >
                              {formatCents(l.amountCents)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ))}
            </section>
          )}
        </>
      )}
    </div>
  )
}

function CheckBanner({ ok, okText, koText }: { ok: boolean; okText: string; koText: string }) {
  return (
    <div
      className={[
        'rounded-lg border p-3 text-sm flex items-start gap-2',
        ok
          ? 'bg-emerald-500/10 border-emerald-500/40 text-emerald-700 dark:text-emerald-300'
          : 'bg-amber-500/10 border-amber-500/40 text-amber-700 dark:text-amber-300',
      ].join(' ')}
    >
      {ok ? <Check size={15} className="mt-0.5 shrink-0" /> : <AlertCircle size={15} className="mt-0.5 shrink-0" />}
      <p>{ok ? okText : koText}</p>
    </div>
  )
}

function Metric({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return (
    <div className={`rounded-lg border border-base p-3 ${accent ? 'bg-toga-500/10' : ''}`}>
      <div className="text-[10px] uppercase tracking-wider t-muted">{label}</div>
      <div className="text-lg font-semibold t-primary tabular-nums">{value}</div>
    </div>
  )
}

function DetailRow({ label, value }: { label: string; value: number }) {
  return (
    <div className="flex items-center justify-between t-secondary">
      <span className="text-xs">{label}</span>
      <span className="tabular-nums text-xs font-mono">{formatCents(value)}</span>
    </div>
  )
}
